import { useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useToast } from '@/components/ui/toast';
import type { Approacher } from '../types';
import {
  buildPayload,
  parseError,
  uploadPhoto,
  type CreateOutcome,
} from './create-new-member-helpers';
import type { NewMemberData } from './use-new-member-form';

export type { CreateOutcome } from './create-new-member-helpers';

type CreateRes = { id: string; player_number?: number };

type Args = {
  data: NewMemberData;
  forcedStatus: string;
  photo: globalThis.File | null;
};

async function callCreate(args: Args): Promise<CreateOutcome> {
  try {
    const res = await pb.send<CreateRes>('/api/team-members/create', {
      method: 'POST',
      body: buildPayload(args.data, args.forcedStatus),
    });
    if (args.photo) await uploadPhoto(res.id, args.photo);
    return res.player_number
      ? { ok: true, id: res.id, player_number: res.player_number }
      : { ok: true, id: res.id };
  } catch (err) {
    return parseError(err);
  }
}

function createdMessage(n?: number): string {
  return n ? `Jugador #${n} creado` : 'Miembro creado';
}

// Warm the detail-modal cache so the post-create redirect lands on
// populated data instead of flashing a skeleton.
async function prefetchDetail(qc: QueryClient, id: string): Promise<void> {
  await qc.prefetchQuery({
    queryKey: ['approacher', id],
    queryFn: () => pb.collection('team_members').getOne<Approacher>(id),
  });
}

export function useCreateNewMember(): {
  submit: (args: Args) => Promise<CreateOutcome>;
  pending: boolean;
} {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [pending, setPending] = useState(false);
  const submit = async (args: Args): Promise<CreateOutcome> => {
    setPending(true);
    const out = await callCreate(args);
    if (out.ok) {
      qc.invalidateQueries({ queryKey: ['team-members'] });
      qc.invalidateQueries({ queryKey: ['team-members-admin'] });
      await prefetchDetail(qc, out.id);
      toast(createdMessage(out.player_number), 'success');
    } else if (out.message) {
      toast(out.message, 'error');
    }
    setPending(false);
    return out;
  };
  return { submit, pending };
}
